import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from './firebase';
import './StockAllocationModal.css';

function StockAllocationModal({ onClose, onContinueToOrder, onAllocationComplete }) {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [view, setView] = useState('check'); // 'check', 'allocate', 'success'
  
  const [availableStock, setAvailableStock] = useState([]);
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState({});
  const [selectedSizes, setSelectedSizes] = useState({});
  
  const [allocatedCount, setAllocatedCount] = useState(0);
  const [remainingCount, setRemainingCount] = useState(0);

  const tshirtSizes = [
    '2-3 YEARS',
    '4-5 YEARS',
    '5-6 YEARS',
    '7-8 YEARS',
    '9-10 YEARS',
    '11-12 YEARS',
    '13-14 YEARS',
    'S',
    'M',
    'L'
  ];

  useEffect(() => {
    loadStockAndStudents();
  }, []);

  const loadStockAndStudents = async () => {
    setLoadingData(true);
    try {
      await Promise.all([
        loadAvailableStock(),
        loadEligibleStudents()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading stock data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const loadAvailableStock = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const q = query(
        collection(db, 'tshirtStock'),
        where('franchiseeRef', '==', franchiseeRef),
        where('remaining', '>', 0)
      );
      
      const snapshot = await getDocs(q);
      const stock = [];
      
      snapshot.forEach(docSnap => {
        stock.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      
      setAvailableStock(stock);
    } catch (error) {
      console.error('Error loading stock:', error);
      throw error;
    }
  };

  const loadEligibleStudents = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const q = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const students = [];
      const initialSizes = {};
      
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        
        // Skip if already ordered
        if (data.tshirtOrdered === true) {
          return;
        }
        
        // Check if T-shirt included
        const tshirtIncluded = data.adminFeeBreakdown?.tshirtIncluded || data.adminFee === 160;
        
        if (tshirtIncluded) {
          const adminFee = data.adminFee || 0;
          const adminFeePaid = data.termPayments?.admin || 0;
          
          // Only include if admin fee fully paid
          if (adminFeePaid >= adminFee) {
            students.push({
              id: docSnap.id,
              ...data
            });
            
            // Pre-fill size if exists
            if (data.tShirtSize) {
              let normalizedSize = data.tShirtSize;
              
              if (!normalizedSize.includes('YEARS') && 
                  !['S', 'M', 'L', 'XL', 'XXL', 'XXXL'].includes(normalizedSize)) {
                normalizedSize = `${normalizedSize} YEARS`;
              }
              
              initialSizes[docSnap.id] = normalizedSize;
            }
          }
        }
      });
      
      students.sort((a, b) => {
        if (a.schoolName !== b.schoolName) {
          return a.schoolName.localeCompare(b.schoolName);
        }
        return a.studentName.localeCompare(b.studentName);
      });
      
      setEligibleStudents(students);
      setSelectedSizes(initialSizes);
    } catch (error) {
      console.error('Error loading students:', error);
      throw error;
    }
  };

  // Sum remaining across ALL stock docs for this size (may span multiple orders)
  const getStockBySize = (size) => {
    return availableStock
      .filter(s => s.size === size)
      .reduce((sum, s) => sum + (s.remaining || 0), 0);
  };

  // Merged view for display — one tile per size, totals combined
  const groupedStock = Object.values(
    availableStock.reduce((acc, s) => {
      if (!acc[s.size]) acc[s.size] = { size: s.size, remaining: 0 };
      acc[s.size].remaining += (s.remaining || 0);
      return acc;
    }, {})
  ).sort((a, b) => {
    const order = ['2-3 YEARS','4-5 YEARS','5-6 YEARS','7-8 YEARS','9-10 YEARS','11-12 YEARS','13-14 YEARS','S','M','L'];
    const ai = order.indexOf(a.size); const bi = order.indexOf(b.size);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const getTotalStockCount = () => {
    return availableStock.reduce((sum, stock) => sum + stock.remaining, 0);
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const handleSizeChange = (studentId, size) => {
    setSelectedSizes(prev => ({
      ...prev,
      [studentId]: size
    }));
  };

  const getSelectedCount = () => {
    return Object.values(selectedStudents).filter(Boolean).length;
  };

  const canAllocateSize = (size) => {
    return getStockBySize(size) > 0;
  };

  const validateAllocation = () => {
    const selected = Object.entries(selectedStudents)
      .filter(([_, isSelected]) => isSelected)
      .map(([studentId]) => studentId);
    
    if (selected.length === 0) {
      alert('Please select at least one student to allocate stock.');
      return false;
    }

    // Check all selected students have sizes
    const studentsWithoutSizes = selected.filter(studentId => !selectedSizes[studentId]);
    if (studentsWithoutSizes.length > 0) {
      alert(`Please select sizes for all selected students. ${studentsWithoutSizes.length} students need sizes.`);
      return false;
    }

    // Check all sizes have stock
    const studentsWithNoStock = selected.filter(studentId => {
      const size = selectedSizes[studentId];
      return !canAllocateSize(size);
    });
    
    if (studentsWithNoStock.length > 0) {
      alert('Some selected students have sizes with no stock available. Please deselect them or choose different sizes.');
      return false;
    }

    // Check stock limits per size
    const sizeAllocationCount = {};
    selected.forEach(studentId => {
      const size = selectedSizes[studentId];
      sizeAllocationCount[size] = (sizeAllocationCount[size] || 0) + 1;
    });

    for (const [size, count] of Object.entries(sizeAllocationCount)) {
      const available = getStockBySize(size);
      if (count > available) {
        alert(`Not enough stock for size ${size}. You're trying to allocate ${count} but only ${available} available.`);
        return false;
      }
    }

    return true;
  };

  const handleAllocate = async () => {
    if (!validateAllocation()) {
      return;
    }

    const selected = Object.entries(selectedStudents)
      .filter(([_, isSelected]) => isSelected)
      .map(([studentId]) => studentId);

    const confirmMessage = `Allocate stock to ${selected.length} students?\n\nThis will mark them as ordered and reduce your stock.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);

    try {
      const batch = writeBatch(db);
      const user = auth.currentUser;
      const now = new Date();

      // Group allocations by size
      const allocationsBySize = {};
      selected.forEach(studentId => {
        const size = selectedSizes[studentId];
        if (!allocationsBySize[size]) {
          allocationsBySize[size] = [];
        }
        allocationsBySize[size].push(studentId);
      });

      // Update stock documents — drain across multiple docs if same size spans several orders
      // Track which stock doc each student is allocated from (for the student record)
      const studentStockDocMap = {}; // studentId -> stockDoc id

      for (const [size, studentIds] of Object.entries(allocationsBySize)) {
        // Get all stock docs for this size, oldest/most remaining first
        const stockDocs = availableStock
          .filter(s => s.size === size && s.remaining > 0)
          .sort((a, b) => (b.remaining || 0) - (a.remaining || 0));

        // Drain students across docs in order
        let studentQueue = [...studentIds];
        for (const stockDoc of stockDocs) {
          if (studentQueue.length === 0) break;

          // How many can this doc satisfy?
          const canFill = Math.min(stockDoc.remaining, studentQueue.length);
          const filledStudents = studentQueue.splice(0, canFill);

          // Record which doc each student comes from
          filledStudents.forEach(sid => { studentStockDocMap[sid] = stockDoc.id; });

          const newAllocations = filledStudents.map(studentId => {
            const student = eligibleStudents.find(s => s.id === studentId);
            return {
              studentRef: doc(db, 'students', studentId),
              studentName: student.studentName,
              allocatedDate: now,
              allocatedBy: 'franchisee'
            };
          });

          batch.update(doc(db, 'tshirtStock', stockDoc.id), {
            allocated: (stockDoc.allocated || 0) + filledStudents.length,
            remaining: stockDoc.remaining - filledStudents.length,
            allocations: [...(stockDoc.allocations || []), ...newAllocations],
            updatedAt: now
          });
        }
      }

      // Update student documents
      selected.forEach(studentId => {
        const student = eligibleStudents.find(s => s.id === studentId);
        const size = selectedSizes[studentId];
        const stockDocId = studentStockDocMap[studentId]
          || (availableStock.find(s => s.size === size) || {}).id;

        const studentRef = doc(db, 'students', studentId);
        batch.update(studentRef, {
          tshirtOrdered: true,
          tshirtSource: 'stock',
          tShirtSize: size,
          stockAllocationId: stockDocId,
          stockAllocationDate: now,
          updatedAt: now
        });
      });

      await batch.commit();

      setAllocatedCount(selected.length);
      setRemainingCount(eligibleStudents.length - selected.length);
      setView('success');

      // Notify parent component
      if (onAllocationComplete) {
        onAllocationComplete(selected);
      }

    } catch (error) {
      console.error('Error allocating stock:', error);
      alert('Error allocating stock. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToOrder = () => {
    if (onContinueToOrder) {
      onContinueToOrder();
    }
  };

  if (loadingData) {
    return (
      <div className="stock-allocation-overlay">
        <div className="stock-allocation-container">
          <div className="loading">Loading stock information...</div>
        </div>
      </div>
    );
  }

  // VIEW 1: STOCK CHECK
  if (view === 'check') {
    return (
      <div className="stock-allocation-overlay">
        <div className="stock-allocation-container stock-check-view">
          <div className="stock-check-header">
            <h1>📦 Stock Available!</h1>
            <button onClick={onClose} className="btn-close">X</button>
          </div>

          <div className="stock-check-content">
            <p className="stock-check-intro">
              You have {getTotalStockCount()} T-shirts in stock. 
              Allocate them to students before ordering new ones!
            </p>

            <div className="stock-summary">
              <h3>Available Stock by Size:</h3>
              <div className="stock-grid">
                {groupedStock.map(stock => (
                  <div key={stock.size} className="stock-item">
                    <span className="stock-size">{stock.size}</span>
                    <span className="stock-count">{stock.remaining} available</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="students-summary">
              <h3>Students Ready:</h3>
              <p className="students-count">{eligibleStudents.length} students with paid admin fees</p>
            </div>
          </div>

          <div className="stock-check-footer">
            <button 
              onClick={() => setView('allocate')} 
              className="btn-primary"
            >
              Allocate Stock First
            </button>
            <button 
              onClick={handleContinueToOrder} 
              className="btn-secondary"
            >
              Skip - Order All New
            </button>
          </div>
        </div>
      </div>
    );
  }

  // VIEW 2: ALLOCATION INTERFACE
  if (view === 'allocate') {
    return (
      <div className="stock-allocation-overlay">
        <div className="stock-allocation-container allocation-view">
          <div className="allocation-header">
            <h1>Allocate Stock to Students</h1>
            <button onClick={onClose} className="btn-close">X</button>
          </div>

          <div className="allocation-content">
            {/* Available Stock Summary */}
            <div className="stock-summary-bar">
              <strong>Available Stock:</strong>
              {groupedStock.map((stock, index) => (
                <span key={stock.size} className="stock-badge">
                  {stock.size}: {stock.remaining}
                  {index < groupedStock.length - 1 && ' | '}
                </span>
              ))}
            </div>

            {/* Student List */}
            <div className="students-allocation-list">
              <table className="allocation-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Select</th>
                    <th>Student Name</th>
                    <th>School</th>
                    <th>T-Shirt Size</th>
                    <th>Stock Status</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleStudents.map(student => {
                    const selectedSize = selectedSizes[student.id];
                    const hasStock = selectedSize ? canAllocateSize(selectedSize) : false;
                    const isSelected = selectedStudents[student.id];

                    return (
                      <tr key={student.id} className={isSelected ? 'selected-row' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected || false}
                            onChange={() => handleStudentToggle(student.id)}
                            className="student-checkbox"
                          />
                        </td>
                        <td>{student.studentName}</td>
                        <td>{student.schoolName}</td>
                        <td>
                          <select
                            className="size-select"
                            value={selectedSize || ''}
                            onChange={(e) => handleSizeChange(student.id, e.target.value)}
                          >
                            <option value="">-- Select Size --</option>
                            {tshirtSizes.map(size => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {!selectedSize ? (
                            <span className="status-warning">⚠️ Select size</span>
                          ) : hasStock ? (
                            <span className="status-available">✅ Available ({getStockBySize(selectedSize)} in stock)</span>
                          ) : (
                            <span className="status-unavailable">✅ No stock</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Allocation Summary */}
            <div className="allocation-summary">
              <strong>Selected:</strong> {getSelectedCount()} of {eligibleStudents.length} students
            </div>
          </div>

          <div className="allocation-footer">
            <button 
              onClick={() => setView('check')} 
              className="btn-cancel"
              disabled={loading}
            >
              Back
            </button>
            <button 
              onClick={handleAllocate} 
              className="btn-submit"
              disabled={loading || getSelectedCount() === 0}
            >
              {loading ? 'Allocating...' : `Allocate Selected (${getSelectedCount()})`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // VIEW 3: SUCCESS
  if (view === 'success') {
    return (
      <div className="stock-allocation-overlay">
        <div className="stock-allocation-container success-view">
          <div className="success-header">
            <h1>✅ Stock Allocated!</h1>
          </div>

          <div className="success-content">
            <div className="success-icon">🎉</div>
            <h2>{allocatedCount} students got shirts from stock</h2>
            
            {remainingCount > 0 ? (
              <p className="remaining-message">
                {remainingCount} students still need shirts.
                Would you like to place an order for them?
              </p>
            ) : (
              <p className="all-done-message">
                All eligible students have been allocated shirts!
              </p>
            )}
          </div>

          <div className="success-footer">
            <button onClick={onClose} className="btn-secondary">
              Done
            </button>
            {remainingCount > 0 && (
              <button onClick={handleContinueToOrder} className="btn-primary">
                Order for Remaining {remainingCount}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default StockAllocationModal;